import { useEffect, useState } from "react";
import PropTypes from "prop-types";

/** Lib Imports */
import { Timeline } from "primereact/timeline";
import moment from "moment";

/** ENUM */
import JobRequisitionStatus from '../../../enums/JobRequisitionStatus';

/** RTK Query */
import { useLazyGetDataQuery } from "../../../app/services/commonApi";

/** CSS */
import "./RequisitionHistory.scss";
import { Card } from "primereact/card";

const getApprovalLevelName = (level) => {
    switch (level) {
        case 1:
            return "Interviewer";
        case 2:
            return "HR";
        case 3:
            return "Admin";
        default:
            return "Requester";
    }
};
const getStatusClass = (statusId) => {
    switch (statusId) {
        case JobRequisitionStatus.DRAFT: return 'status-draft';
        case JobRequisitionStatus.PENDING_APPROVAL: return 'status-pending';
        case JobRequisitionStatus.APPROVED: return 'status-approved';
        case JobRequisitionStatus.REJECTED: return 'status-rejected';
        case JobRequisitionStatus.CANCELLED: return 'status-rejected';
        case JobRequisitionStatus.ON_HOLD: return 'status-pending';
        default: return 'status-draft';
    }
};

const RequisitionHistory = ({ jobRequisitionId }) => {
    const [historyData, setHistoryData] = useState([]);
    const [getRequisitionHistory,
        {
            data: requisitionHistoryData,
            isFetching: requisitionHistoryFetching,
            isSuccess: requisitionHistorySuccess
        }] = useLazyGetDataQuery();

    useEffect(() => {
        if (jobRequisitionId) {
            getRequisitionHistory({
                url: '/JobRequisition/GetRequisitionHistory',
                data: Number(jobRequisitionId)
            });
        }
    }, [jobRequisitionId, getRequisitionHistory]);

    useEffect(() => {
        if (!requisitionHistoryFetching && requisitionHistorySuccess && requisitionHistoryData) {
            const sortedData = [...requisitionHistoryData.data].reverse();
            const uniqueData = [];
            const seen = new Set();

            sortedData.forEach(item => {
                const key = item.description + item.currentStatus + item.changedAt + item.approvalLevel;

                if (!seen.has(key)) {
                    seen.add(key);
                    uniqueData.push(item);
                }
            });

            const mappedData = uniqueData.map((item) => ({
                approver: item.changedByName || `User ${item.changedBy}`,
                approverRole: item.changedByRoleName || '',
                level: item.approvalLevel,
                levelName: getApprovalLevelName(item.approvalLevel),
                action: item.description || 'Updated',
                statusFrom: item.previousStatusName || '-',
                statusTo: item.currentStatusName || '-',
                comments: item.rejectionReason || '',
                timestamp: moment(item.changedAt).format('DD MMM YYYY, hh:mm A'),
                statusClass: getStatusClass(item.currentStatus)
            }));

            setHistoryData(mappedData);
        } else {
            setHistoryData([]);
        }

    }, [requisitionHistoryData, requisitionHistoryFetching, requisitionHistorySuccess]);

    // ✅ Marker UI
    const customizedMarker = (item) => {
        return (
            <span className={`custom-marker ${item.statusClass}`}>

            </span>
        );
    };

    const customizedContent = (item) => {
        return (
            <Card className=" history-timeline-card ">
                <div className="history-content">
                    <div className="history-main">
                        <span className="approver-info">
                            {item.approver}{item.approverRole && ` (${item.approverRole})`}
                        </span>
                        <span className="history-timestamp">
                            {item.timestamp}
                        </span>
                    </div>

                    <div className="history-details">
                        <div>
                            <strong>{item.action}  </strong>
                        </div>
                        <div>
                            <strong>Status:</strong> {item.statusFrom}  <i className="pi pi-arrow-right mx-2"></i> {item.statusTo}
                        </div>
                        <div className="approval-level">
                            <strong>Current Approval Level:</strong> {item.levelName}
                        </div>
                    </div>

                    {item.comments && (
                        <div className="history-comment-box">
                            {item.comments}
                        </div>
                    )}
                </div>
            </Card>

        );
    };

    const renderHistory = () => {
        if (requisitionHistoryFetching) {
            return <div>Loading history...</div>;
        }

        if (historyData.length > 0) {
            return (
                <Timeline
                    value={historyData}
                    align="left"
                    className="customized-timeline"
                    marker={customizedMarker}
                    content={customizedContent}
                />
            );
        }

        return <div>No history available for this requisition.</div>;
    };

    return (
        <div className="requisition-history-container">
            
            {renderHistory()}
        </div>
    );
};

RequisitionHistory.propTypes = {
    jobRequisitionId: PropTypes.number.isRequired,
};

export default RequisitionHistory;